// src/pages/PrivacyPolicy.tsx

import React from 'react';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';

const PrivacyPolicy: React.FC = () => {
  const analytics = useAnalyticsWithConsent();

  analytics.trackPixelPageView();
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-4xl font-bold mb-6">Privacy Policy</h1>

      <section className="mb-8">
        <p className="mb-4">
          At RentSmart.ie, we are committed to protecting your privacy and
          ensuring the security of your personal information. This Privacy
          Policy outlines our practices concerning the collection, use, and
          sharing of your data when you use our website and services.
        </p>
        <p>Last updated: 17/11/2025</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          1. Information We Collect
        </h2>
        <p className="mb-4">We collect the following types of information:</p>

        <h3 className="text-xl font-semibold mb-3">1.1 Personal Information</h3>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Account Information:</strong> Name, email address, phone
            number, password (encrypted), profile photo
          </li>
          <li>
            <strong>Address Information:</strong> Postal address, location data
            (for renters and landlords)
          </li>
          <li>
            <strong>Identity Verification Data :</strong> Government-issued
            ID (passport, driving license, national ID), selfie photos for
            identity verification, PSRA license numbers for property agents
          </li>
          <li>
            <strong>Business Verification Data (KYBC):</strong> Company
            registration details, business licenses, tax identification numbers,
            proof of property ownership or management authority
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          1.2 Property and Listing Information
        </h3>
        <ul className="list-disc pl-6 mb-4">
          <li>Property details, descriptions, pricing, and availability</li>
          <li>Property photos and virtual tour links</li>
          <li>Building Energy Rating (BER) information</li>
          <li>
            Rental history and tenancy records (for landlord toolkit features)
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          1.3 Communication and Interaction Data
        </h3>
        <ul className="list-disc pl-6 mb-4">
          <li>Messages sent through our platform between users</li>
          <li>Inquiry forms and application submissions</li>
          <li>Customer support correspondence</li>
          <li>Feedback and survey responses</li>
          <li>
            <strong>Content Moderation Data (DSA):</strong> Reports
            submitted, moderation decisions, appeal submissions, and outcomes
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          1.4 Usage and Technical Data
        </h3>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Browsing Activity:</strong> Pages viewed, search queries,
            listings bookmarked, time spent on pages, click patterns
          </li>
          <li>
            <strong>Device Information:</strong> IP address, browser type,
            operating system, device identifiers, screen resolution
          </li>
          <li>
            <strong>Cookies and Tracking:</strong> Session cookies, preference
            cookies, analytics cookies (see Cookie Policy for details)
          </li>
          <li>
            <strong>Accessibility Data :</strong> Accessibility features
            used (screen reader compatibility, keyboard navigation, high
            contrast mode), to improve platform accessibility
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          1.5 Financial and Transaction Data
        </h3>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Rent Payment Tracking (Landlord Toolkit):</strong>{' '}
            Rent payment records, payment due dates, late payment tracking
            (stored securely for landlord management purposes)
          </li>
          <li>
            <strong>Note:</strong> We do not process actual payments through our
            platform. All financial transactions occur between landlords and
            tenants directly.
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          2. How We Use Your Information
        </h2>
        <p className="mb-4">
          We use your information for the following purposes:
        </p>

        <h3 className="text-xl font-semibold mb-3">2.1 Service Provision</h3>
        <ul className="list-disc pl-6 mb-4">
          <li>Create and manage your account</li>
          <li>Enable property listings and search functionality</li>
          <li>Facilitate communication between landlords and renters</li>
          <li>Process inquiries and rental applications</li>
          <li>
            Provide landlord toolkit features (rent tracking, tenant management)
          </li>
          <li>Enable Auto-Apply feature for eligible renters</li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          2.2 Safety and Verification
        </h3>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Identity Verification:</strong> Verify user identities to
            prevent fraud and ensure platform safety
          </li>
          <li>
            <strong>KYBC Compliance:</strong> Verify business credentials for
            landlords and property agents
          </li>
          <li>
            <strong>Content Moderation (DSA):</strong> Review reported content,
            investigate violations, enforce Terms of Service, maintain platform
            safety
          </li>
          <li>
            <strong>Risk Assessment:</strong> Detect and prevent fraudulent
            activity, scams, and terms violations
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">2.3 Communication</h3>
        <ul className="list-disc pl-6 mb-4">
          <li>
            Send transactional emails (account notifications, inquiry
            confirmations, verification emails)
          </li>
          <li>Provide customer support and respond to inquiries</li>
          <li>
            Send marketing communications (with your consent, opt-out available)
          </li>
          <li>
            Send newsletter with property recommendations (opt-in required)
          </li>
          <li>Notify you of Terms of Service or Privacy Policy changes</li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          2.4 Personalization and Improvement
        </h3>
        <ul className="list-disc pl-6 mb-4">
          <li>
            Personalize your experience with relevant property recommendations
          </li>
          <li>Improve our services, features, and user interface</li>
          <li>
            <strong>Accessibility Enhancement:</strong> Analyze usage of
            accessibility features to improve platform accessibility for users
            with disabilities
          </li>
          <li>Conduct analytics to understand user behavior and preferences</li>
          <li>Develop new features and services</li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">2.5 Legal Compliance</h3>
        <ul className="list-disc pl-6 mb-4">
          <li>
            Comply with legal obligations (GDPR, DSA, Consumer Rights Act 2022)
          </li>
          <li>Respond to legal requests from authorities</li>
          <li>Enforce our Terms of Service and Community Guidelines</li>
          <li>Protect our rights, property, and safety</li>
          <li>
            Maintain records for tax and accounting purposes (where applicable)
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          2.6 Legal Basis for Processing (GDPR)
        </h3>
        <p className="mb-4">
          We process your personal data based on the following legal grounds:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Contract Performance:</strong> Processing necessary to
            provide services you've requested (account creation, listing
            management, messaging)
          </li>
          <li>
            <strong>Legitimate Interest:</strong> Fraud prevention, platform
            security, service improvement, business analytics (balanced against
            your rights)
          </li>
          <li>
            <strong>Consent:</strong> Marketing communications, optional
            features, cookies (withdrawable at any time)
          </li>
          <li>
            <strong>Legal Obligation:</strong> Compliance with laws, responding
            to legal requests, DSA content moderation requirements
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          3. Data Retention and Deletion
        </h2>
        <p className="mb-4">
          In compliance with GDPR Article 5(1)(e) - the "storage limitation"
          principle - we only retain personal data for as long as necessary for
          the purposes for which it was collected. We have implemented automated
          data retention policies:
        </p>

        <h3 className="text-xl font-semibold mb-3">
          3.1 Automated Deletion Policies
        </h3>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Guest Inquiries:</strong> Inquiries from non-registered
            users are automatically deleted after 1 year. This ensures we only retain
            active communication threads.
          </li>
          <li>
            <strong>Unsuccessful Applications:</strong> Auto-apply applications
            with "pending" or "failed" status are automatically deleted after 1
            year. This minimizes retention of unsuccessful tenancy attempts.
          </li>
          <li>
            <strong>Successful Tenancies:</strong> Data related to successful
            tenancies is retained for 7 years after the tenancy ends, as
            required by Irish tax law and for potential legal disputes.
          </li>
          <li>
            <strong>Active Accounts:</strong> Personal data for active user
            accounts is retained as long as the account remains active and for a
            reasonable period after account closure.
          </li>
          <li>
            <strong>Inactive Accounts:</strong> Accounts inactive for 24+
            months may be deleted after email notification (30 days notice to
            reactivate).
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          3.2 Content Moderation Data Retention
        </h3>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Reports and Decisions:</strong> Content moderation reports,
            decisions, and appeals are retained for 6 months after case closure
            (DSA compliance requirement)
          </li>
          <li>
            <strong>Serious Violations:</strong> Records of account suspensions
            or bans retained for 2 years for repeat offender detection
          </li>
          <li>
            <strong>Legal Requests:</strong> Data related to legal
            investigations retained as required by law (typically 7 years)
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          3.3 Identity Verification Data 
        </h3>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Verified Users:</strong> Identity documents retained for
            account lifetime + 1 year after closure
          </li>
          <li>
            <strong>Failed Verification:</strong> Rejected verification
            documents deleted within 30 days
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          3.4 Manual Deletion Requests
        </h3>
        <p className="mb-4">
          Our automated cleanup process runs monthly to ensure compliance with
          these retention periods. You can request immediate deletion of your
          data at any time by exercising your "Right to Erasure" (see Section
          6).
        </p>
        <p className="mb-4">
          <strong>Note:</strong> Some data may be retained longer if required by
          law (e.g., financial records for tax purposes, records subject to
          legal hold).
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          4. Data Sharing and Disclosure
        </h2>
        <p className="mb-4">We may share your information with:</p>

        <h3 className="text-xl font-semibold mb-3">4.1 Other Users</h3>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Landlords:</strong> When you inquire about or apply for a
            property, landlords can see your name, email, phone number (if
            provided), and inquiry message
          </li>
          <li>
            <strong>Renters:</strong> Landlords' contact information is
            displayed on listings according to their privacy preferences
          </li>
          <li>
            <strong>Public Profiles:</strong> Your profile name and photo may be
            visible to other users
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">4.2 Service Providers</h3>
        <p className="mb-4">
          We share data with trusted third-party service providers who assist in
          our operations:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Cloud Infrastructure:</strong> Google Cloud Platform
            (Firebase) for hosting and data storage
          </li>
          <li>
            <strong>Email Services:</strong> Mailgun for transactional email
            delivery
          </li>
          <li>
            <strong>AI Services:</strong> OpenAI for AI-powered listing
            content review for inappropriate content
          </li>
          <li>
            <strong>Analytics:</strong> Google Analytics, Facebook Pixel (with
            cookie consent)
          </li>
        </ul>
        <p className="mb-4">
          All service providers are bound by data processing agreements and
          process data only on our instructions.
        </p>

        <h3 className="text-xl font-semibold mb-3">4.3 Legal Authorities</h3>
        <p className="mb-4">
          We may disclose your information when required by law or to:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            Comply with legal obligations, court orders, or regulatory requests
          </li>
          <li>Enforce our Terms of Service or investigate violations</li>
          <li>Protect our rights, property, or safety, or that of our users</li>
          <li>Prevent fraud, illegal activity, or security threats</li>
          <li>
            <strong>DSA Compliance:</strong> Report illegal content to
            Irish or EU authorities as required by law
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">4.4 Business Transfers</h3>
        <p className="mb-4">
          In the event of a merger, acquisition, or sale of assets, your
          personal data may be transferred to the acquiring entity. We will
          provide notice and obtain consent if required by law.
        </p>

        <h3 className="text-xl font-semibold mb-3">4.5 What We Don't Share</h3>
        <p className="mb-4 font-semibold">
          We do not sell your personal information to third parties for
          marketing purposes.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">5. Data Security</h2>
        <p className="mb-4">
          We implement appropriate technical and organizational measures to
          protect your personal information against unauthorized or unlawful
          processing, accidental loss, destruction, or damage.
        </p>

        <h3 className="text-xl font-semibold mb-3">
          5.1 Technical Security Measures
        </h3>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Encryption:</strong> Data in transit protected with TLS/SSL
            encryption
          </li>
          <li>
            <strong>Identity Documents:</strong> Government IDs and
            verification documents stored with security
            access controls
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          5.2 Data Breach Notification
        </h3>
        <p className="mb-4">
          In the event of a data breach that poses a risk to your rights and
          freedoms, we will:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            Notify the Irish Data Protection Commission within 72 hours of
            discovery
          </li>
          <li>Notify affected users without undue delay via email</li>
          <li>
            Provide information on the nature of the breach and steps taken to
            mitigate harm
          </li>
          <li>Offer guidance on protective measures you can take</li>
        </ul>

        <p className="mb-4">
          <strong>Your Responsibility:</strong> While we implement robust
          security measures, you are responsible for maintaining the
          confidentiality of your password and account credentials.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">6. Your Rights</h2>
        <p className="mb-4">Under GDPR, you have the right to:</p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Access your personal data:</strong> Request a copy of the
            personal data we hold about you
          </li>
          <li>
            <strong>Rectify inaccurate personal data:</strong> Request
            corrections to any inaccurate or incomplete information
          </li>
          <li>
            <strong>Request erasure of your personal data:</strong> Request
            immediate deletion of your data (separate from our automated
            retention policies). We will respond within 30 days of a verified
            request.
          </li>
          <li>
            <strong>Object to processing of your personal data:</strong> Object
            to certain types of processing, including direct marketing
          </li>
          <li>
            <strong>
              Request restriction of processing your personal data:
            </strong>
            Request temporary suspension of data processing
          </li>
          <li>
            <strong>Request transfer of your personal data:</strong> Request
            your data in a portable format to transfer to another service
            provider
          </li>
          <li>
            <strong>Withdraw consent:</strong> Withdraw consent for data
            processing at any time where consent was the legal basis
          </li>
        </ul>
        <p className="mb-4">
          To exercise any of these rights, please contact us at{' '}
          <a
            href="mailto:privacy@rentsmart.ie"
            className="text-blue-500 hover:underline"
          >
            privacy@rentsmart.ie
          </a>{' '}
          or{' '}
          <a
            href="mailto:dpo@rentsmart.ie"
            className="text-blue-500 hover:underline"
          >
            dpo@rentsmart.ie
          </a>
          . We will verify your identity before processing your request.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          7. Cookies and Tracking Technologies
        </h2>
        <p className="mb-4">
          We use cookies and similar tracking technologies to collect
          information about your browsing activities. You can manage your cookie
          preferences through your browser settings. For more information,
          please see our{' '}
          <a href="/cookie-policy" className="text-blue-500 hover:underline">
            Cookie Policy
          </a>
          .
        </p>

        <h3 className="text-xl font-semibold mb-3">
          7.1 Types of Cookies We Use
        </h3>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>Essential Cookies:</strong> Required for platform
            functionality (authentication, security)
          </li>
          <li>
            <strong>Analytics Cookies:</strong> Google Analytics, Firebase
            Analytics (with consent)
          </li>
          <li>
            <strong>Marketing Cookies:</strong> Facebook Pixel for advertising
            (opt-in required)
          </li>
          <li>
            <strong>Preference Cookies:</strong> Remember your settings and
            preferences
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          7.2 Cookie Consent Management
        </h3>
        <p className="mb-4">
          We obtain your consent before using non-essential cookies. You can
          withdraw consent at any time through our{' '}
          <a
            href="/cookie-preferences"
            className="text-blue-500 hover:underline"
          >
            Cookie Preferences
          </a>{' '}
          page.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          7A. International Data Transfers
        </h2>
        <p className="mb-4">
          RentSmart.ie is based in Ireland (European Economic Area). However,
          some of our service providers may process data outside the EEA:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            <strong>OpenAI:</strong> AI-powered listing review for inappropriate
            content
            (Standard Contractual Clauses in place)
          </li>
          <li>
            <strong>Google Cloud Platform:</strong> Data stored in EU region
            (europe-west1)
          </li>
          <li>
            <strong>Mailgun:</strong> Email services (EU data centers,
            GDPR-compliant)
          </li>
        </ul>
        <p className="mb-4">
          For transfers outside the EEA, we ensure adequate protection through:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>
            Standard Contractual Clauses approved by the European Commission
          </li>
          <li>Data Processing Agreements with GDPR compliance obligations</li>
          <li>Adequacy decisions for certain countries (where applicable)</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          8. Changes to This Policy
        </h2>
        <p className="mb-4">
          We may update this Privacy Policy from time to time. We will notify
          you of any changes by posting the new Privacy Policy on this page and
          updating the "Last updated" date.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4">9. Contact Us</h2>
        <p className="mb-4">
          If you have any questions about this Privacy Policy, data retention,
          or wish to exercise your rights, please contact us at:
        </p>
        <ul className="list-none mb-4">
          <li>
            <strong>General Privacy Inquiries:</strong>{' '}
            <a
              href="mailto:privacy@rentsmart.ie"
              className="text-blue-500 hover:underline"
            >
              privacy@rentsmart.ie
            </a>
          </li>
          <li>
            <strong>Data Protection Officer:</strong>{' '}
            <a
              href="mailto:dpo@rentsmart.ie"
              className="text-blue-500 hover:underline"
            >
              dpo@rentsmart.ie
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
